/**
 * Verification Page — expert/company verification application form.
 * Shows current status if already submitted, or form to apply.
 */

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, BadgeCheck, Clock, CheckCircle2, FileText } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '@turnifly/shared/ui'
import { Input } from '@turnifly/shared/ui'
import { Badge } from '@turnifly/shared/ui'
import { useAuthStore } from '@turnifly/shared/stores'
import { submitVerificationRequest, fetchVerificationStatus } from '@turnifly/shared/api'
import { triggerHapticNotification as hapticNotification } from '@turnifly/shared/utils'
import type { VerificationRequestParams } from '@turnifly/shared/api'

interface VerificationPageProps {
  isOpen: boolean
  onClose: () => void
}

interface VerificationFormData {
  type: 'expert' | 'organization' | 'company'
  display_name: string
  username: string
  website: string
  contact_phone: string
  contact_telegram: string
}

export const VerificationPage: React.FC<VerificationPageProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const queryClient = useQueryClient()
  const [selectedType, setSelectedType] = useState<'expert' | 'company'>('expert')

  const { data: existingRequest } = useQuery({
    queryKey: ['verification', user?.id],
    queryFn: () => fetchVerificationStatus(user!.id),
    enabled: !!user?.id && isOpen,
  })

  const {
    register, handleSubmit, reset, formState: { errors },
  } = useForm<VerificationFormData>({
    defaultValues: { type: 'expert', display_name: '', username: '', website: '', contact_phone: '', contact_telegram: '' },
  })

  useEffect(() => {
    if (isOpen) reset()
  }, [isOpen, reset])

  const submitMutation = useMutation({
    mutationFn: (data: VerificationFormData) => submitVerificationRequest({
      userId: user!.id,
      type: data.type,
      displayName: data.display_name,
      username: data.username,
      website: data.website || undefined,
      documentsUrls: [],
      contactPhone: data.contact_phone || undefined,
      contactTelegram: data.contact_telegram || undefined,
    } satisfies VerificationRequestParams),
    onSuccess: () => {
      hapticNotification('success')
      void queryClient.invalidateQueries({ queryKey: ['verification', user?.id] })
    },
  })

  const onSubmit = (data: VerificationFormData): void => {
    submitMutation.mutate({ ...data, type: selectedType })
  }

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'auto'
    return () => { document.body.style.overflow = 'auto' }
  }, [isOpen])

  const hasExisting = existingRequest && existingRequest.status !== 'rejected'

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />
          <motion.div
            initial={{ y: '100%', opacity: 0.8 }} animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0.8 }}
            transition={{ type: 'tween', duration: 0.25, ease: 'easeInOut' }}
            className="fixed z-50 bg-bg flex flex-col inset-x-0 bottom-0 rounded-t-[32px] max-h-[92vh] lg:inset-auto lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 lg:rounded-3xl lg:max-h-[85vh] lg:w-full lg:max-w-md lg:border lg:border-white/[0.06] lg:shadow-glass"
          >
            <div className="flex items-center justify-between px-5 pt-5 pb-2 shrink-0">
              <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/[0.04] tap-highlight-transparent">
                <X size={20} className="text-text" />
              </button>
              <h2 className="font-display font-bold text-lg text-text">{t('verification.title')}</h2>
              <div className="w-10" />
            </div>

            <div className="flex-1 overflow-y-auto px-5 pb-8">
              {hasExisting ? (
                <ExistingStatus status={existingRequest.status} notes={existingRequest.admin_notes} />
              ) : (
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-2">
                  {/* Type selector */}
                  <div className="flex gap-2">
                    <TypeCard
                      label={t('verification.expert')}
                      description={t('verification.expertDesc')}
                      isActive={selectedType === 'expert'}
                      onClick={() => setSelectedType('expert')}
                    />
                    <TypeCard
                      label={t('verification.company')}
                      description={t('verification.companyDesc')}
                      isActive={selectedType === 'company'}
                      onClick={() => setSelectedType('company')}
                    />
                  </div>

                  <Input
                    label={t('verification.displayName')}
                    placeholder={t('verification.displayNamePlaceholder')}
                    {...register('display_name', { required: true, minLength: 3, maxLength: 40 })}
                    error={errors.display_name ? t('auth.nameMinError') : undefined}
                  />
                  <Input
                    label={t('verification.username')}
                    placeholder="turnifly_expert"
                    {...register('username', { required: true, minLength: 5, maxLength: 32, pattern: /^[a-z0-9_]+$/ })}
                    error={errors.username ? t('verification.usernameError') : undefined}
                    leftIcon={<span className="text-text-hint text-sm">@</span>}
                  />
                  <Input
                    label={t('verification.website')}
                    placeholder="https://"
                    {...register('website')}
                  />
                  <Input
                    label={t('verification.contactPhone')}
                    placeholder="+998 90 123 45 67"
                    {...register('contact_phone')}
                  />
                  <Input
                    label={t('verification.contactTelegram')}
                    placeholder="@username"
                    {...register('contact_telegram')}
                  />

                  {/* Documents note */}
                  <div className="flex items-start gap-2 p-3 rounded-xl bg-white/[0.02] border border-white/[0.04]">
                    <FileText size={16} className="text-text-hint mt-0.5 shrink-0" />
                    <p className="text-xs text-text-hint leading-relaxed">
                      {t('verification.documentsNote')}
                    </p>
                  </div>

                  <Button
                    type="submit" variant="primary" fullWidth
                    isLoading={submitMutation.isPending}
                  >
                    {t('verification.submit')}
                  </Button>
                </form>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

// ── Sub-components ──────────────────────────────────────────

const ExistingStatus: React.FC<{ status: string; notes: string | null }> = ({ status, notes }) => {
  const { t } = useTranslation()
  const statusConfig: Record<string, { icon: React.ReactNode; badge: 'warning' | 'success'; label: string }> = {
    pending: { icon: <Clock size={28} className="text-warning" />, badge: 'warning', label: t('verification.pending') },
    approved: { icon: <CheckCircle2 size={28} className="text-success" />, badge: 'success', label: t('verification.approved') },
  }
  const cfg = statusConfig[status] ?? statusConfig.pending

  return (
    <div className="flex flex-col items-center py-8 text-center">
      <div className="w-16 h-16 rounded-full bg-white/[0.04] flex items-center justify-center mb-4">
        {cfg.icon}
      </div>
      <Badge variant={cfg.badge}>{cfg.label}</Badge>
      <p className="text-sm text-text-hint mt-3 max-w-xs">
        {status === 'pending' ? t('verification.pendingDesc') : t('verification.approvedDesc')}
      </p>
      {notes && (
        <p className="text-sm text-text/70 mt-2 p-3 rounded-xl bg-white/[0.02] border border-white/[0.04]">
          {notes}
        </p>
      )}
    </div>
  )
}

interface TypeCardProps {
  label: string
  description: string
  isActive: boolean
  onClick: () => void
}

const TypeCard: React.FC<TypeCardProps> = React.memo(({ label, description, isActive, onClick }) => (
  <button
    type="button" onClick={onClick}
    className={`flex-1 p-3 rounded-xl text-left transition-all tap-highlight-transparent ${
      isActive
        ? 'bg-primary/10 border border-primary/20'
        : 'bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.05]'
    }`}
  >
    <BadgeCheck size={18} className={isActive ? 'text-primary mb-1.5' : 'text-text-hint mb-1.5'} />
    <p className="text-sm font-semibold text-text">{label}</p>
    <p className="text-[10px] text-text-hint mt-0.5 leading-tight">{description}</p>
  </button>
))
TypeCard.displayName = 'TypeCard'
